import type { ReactNode } from 'react'
import { Pressable, StyleSheet, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { LoadingSpinner } from '../components/LoadingSpinner'
import { FiatActionButton } from './FiatActionButton'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

export type FiatStatusTone = 'waiting' | 'success' | 'failure' | 'notice'

/** Matches the glyph the other tones draw in this slot (`styles.glyph`), so the tones stay level. */
const WAITING_SPINNER_SIZE = 48

export interface FiatStatusScreenProps {
  tone: FiatStatusTone
  title: string
  detail?: string
  /** The state's recovery CTA, when its step response carries one. */
  cta?: { label: string; onPress: () => void }
  secondaryCta?: { label: string; onPress: () => void }
  isSubmitting?: boolean
  onClose?: () => void
}

/**
 * Every in-place fiat status: verifying, processing, terminal outcomes. One
 * screen because the design keeps the whole Apple Pay flow on one canvas —
 * these render where the entry was, not as navigations away from it.
 */
export function FiatStatusScreen({
  tone,
  title,
  detail,
  cta,
  secondaryCta,
  isSubmitting = false,
  onClose,
}: FiatStatusScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height } = useWindowDimensions()
  const insets = useSafeAreaInsets()

  return (
    <Box
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      <Box flex={1} style={styles.center}>
        <StatusGlyph tone={tone} />
        <Text variant="headerTitle" style={styles.title}>
          {title}
        </Text>
        {detail === undefined ? null : (
          <Text
            variant="bodyMedium"
            style={[styles.detail, { color: theme.content.elements.secondary }]}
          >
            {detail}
          </Text>
        )}
      </Box>
      <Box style={[styles.footer, { paddingBottom: insets.bottom + 24 }]}>
        {cta === undefined ? null : (
          <FiatActionButton
            label={cta.label}
            onPress={cta.onPress}
            disabled={isSubmitting}
          />
        )}
        {secondaryCta === undefined ? null : (
          <FiatActionButton
            label={secondaryCta.label}
            onPress={secondaryCta.onPress}
            variant="secondary"
            disabled={isSubmitting}
          />
        )}
        {onClose === undefined ? null : (
          <Pressable onPress={onClose} accessibilityRole="button">
            <Text
              variant="bodyMedium"
              style={[
                styles.close,
                { color: theme.content.elements.secondary },
              ]}
            >
              {t('common.close')}
            </Text>
          </Pressable>
        )}
      </Box>
    </Box>
  )
}

function StatusGlyph({ tone }: { tone: FiatStatusTone }): ReactNode {
  const theme = useTheme()
  if (tone === 'notice') {
    return null
  }
  if (tone === 'waiting') {
    // The SDK's own ring, not the platform's. These screens are visible for as long as the wait
    // lasts — Stripe's sheet sits over one rather than covering it — so the spinner behind a
    // provider's modal is the same one the quote screen's action shows, not an iOS default.
    return (
      <LoadingSpinner
        size={WAITING_SPINNER_SIZE}
        color={theme.content.elements.primary}
      />
    )
  }
  // Text glyphs until the terminal designs land — the design owes these
  // screens their faceplates (rare-KYC + terminals, Neil's list).
  return (
    <Text
      style={[
        styles.glyph,
        {
          color:
            tone === 'success'
              ? theme.content.elements.primary
              : theme.content.elements.error,
        },
      ]}
    >
      {tone === 'success' ? '✓' : '✕'}
    </Text>
  )
}

const styles = StyleSheet.create({
  center: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    gap: 12,
  },
  glyph: { fontSize: 48, lineHeight: 56 },
  title: { textAlign: 'center' },
  detail: { textAlign: 'center' },
  footer: { paddingHorizontal: 24, gap: 12 },
  close: { textAlign: 'center', paddingVertical: 8 },
})
